import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, UserCircle2, LogIn } from "lucide-react";
import Seo from "@/components/Seo";
import { useLang } from "@/lib/lang";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ClientAuth() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ company: "", contact_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "register") {
        await api.post("/auth/register", form);
      } else {
        await api.post("/auth/login", { email: form.email, password: form.password });
      }
      navigate("/client/tableau-de-bord");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy-950 outline-none focus:border-electric";

  return (
    <main className="min-h-screen bg-paper px-5 pb-20 pt-[120px] md:pt-[160px]" data-testid="client-auth-page">
      <Seo title={lang === "fr" ? "Espace client | Export Monde Prestige Inc." : "Client area | Export Monde Prestige Inc."} description={lang === "fr" ? "Suivez vos demandes d'équipement et vos devis en ligne." : "Track your equipment requests and quotes online."} path="/client" />
      <div className="mx-auto w-full max-w-md">
        <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-steel transition-colors hover:text-electric">
          <ArrowLeft size={15} /> {lang === "fr" ? "Retour au site" : "Back to site"}
        </Link>
        <div className="rounded-2xl border border-slate-200/90 bg-white p-9 shadow-card" data-testid="client-auth-card">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-badge text-sky-700"><UserCircle2 size={24} /></span>
          <h1 className="mt-5 font-display text-2xl font-bold text-navy-950">
            {lang === "fr" ? "Espace client" : "Client area"}
          </h1>
          <p className="mt-2 text-sm text-graphite">
            {lang === "fr"
              ? "Suivez vos demandes d'équipement MOON et vos demandes de consultation en ligne."
              : "Track your MOON equipment requests and consultation requests online."}
          </p>

          <div className="mt-6 flex gap-1 rounded-full bg-paper p-1" role="tablist" aria-label={lang === "fr" ? "Connexion ou inscription" : "Sign in or register"}>
            <button role="tab" aria-selected={mode === "login"} onClick={() => setMode("login")} data-testid="client-tab-login"
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold transition-colors ${mode === "login" ? "bg-navy-950 text-white" : "text-graphite"}`}>
              <LogIn size={14} /> {lang === "fr" ? "Connexion" : "Sign in"}
            </button>
            <button role="tab" aria-selected={mode === "register"} onClick={() => setMode("register")} data-testid="client-tab-register"
              className={`inline-flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold transition-colors ${mode === "register" ? "bg-navy-950 text-white" : "text-graphite"}`}>
              <UserCircle2 size={14} /> {lang === "fr" ? "Créer un compte" : "Create account"}
            </button>
          </div>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="client-auth-form">
            {mode === "register" && (
              <>
                <div>
                  <label htmlFor="c-company" className="mb-1.5 block text-[13px] font-bold text-navy-900">{lang === "fr" ? "Entreprise" : "Company"} *</label>
                  <input id="c-company" required data-testid="client-company-input" value={form.company} onChange={set("company")} className={inputCls} />
                </div>
                <div>
                  <label htmlFor="c-name" className="mb-1.5 block text-[13px] font-bold text-navy-900">{lang === "fr" ? "Nom du contact" : "Contact name"} *</label>
                  <input id="c-name" required data-testid="client-name-input" value={form.contact_name} onChange={set("contact_name")} className={inputCls} />
                </div>
              </>
            )}
            <div>
              <label htmlFor="c-email" className="mb-1.5 block text-[13px] font-bold text-navy-900">{lang === "fr" ? "Courriel professionnel" : "Business email"} *</label>
              <input id="c-email" type="email" required data-testid="client-email-input" value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div>
              <label htmlFor="c-password" className="mb-1.5 block text-[13px] font-bold text-navy-900">{lang === "fr" ? "Mot de passe" : "Password"} *</label>
              <input id="c-password" type="password" required minLength={8} data-testid="client-password-input" value={form.password} onChange={set("password")} className={inputCls} />
              {mode === "register" && <p className="mt-1 text-xs text-steel">{lang === "fr" ? "8 caractères minimum." : "8 characters minimum."}</p>}
            </div>
            {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="client-auth-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="client-auth-submit"
              className="w-full rounded-full bg-electric py-3.5 text-sm font-bold text-white transition-colors hover:bg-electric-dark disabled:opacity-60">
              {loading ? "…" : mode === "login" ? (lang === "fr" ? "Se connecter" : "Sign in") : (lang === "fr" ? "Créer mon compte" : "Create my account")}
            </button>
            <p className="text-center text-[11px] leading-relaxed text-steel">
              {lang === "fr"
                ? "Les demandes sont associées au courriel du compte. Voir notre politique de confidentialité."
                : "Requests are linked to the account's email. See our privacy policy."}
            </p>
          </form>
        </div>
      </div>
    </main>
  );
}
